import React, { useState } from "react";
import { FilterSection, AdvancedFilters } from "@/components/ui";
import { SearchBar, FormSelect, Button } from "@/components/ui";
import { Search, X, Plus, Calendar, Filter, RotateCcw } from "lucide-react";
import { fmtDate } from "@/lib/formatters";
import DateFilter from "@/components/ui/DateFilter";

/**
 * InventoryControlPanel Component
 * Refactored to use the standard FilterSection pattern from the new-frontend design system.
 */
export const InventoryControlPanel = ({
  activeTab,
  search, setSearch,
  searchPlaceholder,
  filters, setFilters,
  dynamicSettings,
  onAddItemClick,
  buttonLabel = "New Item",
  onBackClick,
  showBack = false,
  categories: customCategories,
  parties,
  projects,
  partyLabel = "Vendors",
  showStatusFilter = true,
  customStatusOptions = null,
  statusPlaceholder = "All Statuses",
  showVendorFilter = true,
  showCustomerFilter = true,
  showCategoryFilter = true,
  showItemFilter = false,
  masterItems = [],
  onExport,
  isExporting,
  children
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key, value) => {
    if (typeof key === 'object') {
      setFilters(prev => ({ ...prev, ...key }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  // Status options for the standard FilterSection
  const statusOptions = showStatusFilter ? (customStatusOptions || [
    { value: 'in', label: 'In Stock' },
    { value: 'low', label: 'Low Stock' },
    { value: 'out', label: 'Out of Stock' }
  ]) : [];

  return (
    <FilterSection
      search={search}
      setSearch={setSearch}
      searchPlaceholder={searchPlaceholder}
      filters={filters}
      setFilters={(key, val) => handleFilterChange(key, val)}
      onAddClick={onAddItemClick}
      addLabel={buttonLabel}
      statusOptions={statusOptions}
      statusPlaceholder={statusPlaceholder}
      onExport={onExport}
      isExporting={isExporting}
      resetButton={
        <button
          onClick={() => handleFilterChange({
            category: 'all',
            item: 'all',
            supplier: 'all',
            customer: 'all',
            status: 'all',
            date: 'all',
            fromDate: '',
            toDate: ''
          })}
          className="p-1.5 h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-400 hover:bg-white hover:text-brand-blue hover:border-brand-blue/30 transition-all group shrink-0"
          title="Reset Filters"
        >
          <RotateCcw size={14} className="group-hover:rotate-[-45deg] transition-transform" />
        </button>
      }
      // Removed advanced filter logic
    >
      {/* Category Dropdown */}
      {/* {showCategoryFilter !== false && (
      <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 flex items-center gap-2">
        <select
          className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-36"
          value={filters.category || "all"}
          onChange={e => handleFilterChange('category', e.target.value)}
        >
          <option value="all">All Categories</option>
          {(customCategories || dynamicSettings?.cats || []).map(c => {
              const label = c.categoryName || c.title || c.name || (typeof c === 'string' ? c : "");
              const safeLabel = typeof label === 'object' ? "—" : (label || "—");
              return <option key={c._id || String(safeLabel)} value={c._id || String(safeLabel)}>{String(safeLabel)}</option>
          })}
        </select>
      </div>
      )} */}

      {/* Items Dropdown
      {showItemFilter !== false && (
      <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 flex items-center gap-2">
        <select
          className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-36"
          value={filters.item || "all"}
          onChange={e => handleFilterChange('item', e.target.value)}
        >
          <option value="all">All Items</option>
          {masterItems.map(c => {
              const label = c.itemName || c.name || c.title || (typeof c === 'string' ? c : "");
              const safeLabel = typeof label === 'object' ? "—" : (label || "—");
              return <option key={c._id || String(safeLabel)} value={c._id || String(safeLabel)}>{String(safeLabel)}</option>
          })}
        </select>
      </div>
      )} */}

      {/* Vendors Dropdown */}
      {showVendorFilter !== false && (
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 flex items-center gap-2">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-32"
            value={filters.supplier || "all"}
            onChange={e => handleFilterChange('supplier', e.target.value)}
          >
            <option value="all">All Vendors</option>
            {(() => {
              const vList = parties ? parties.filter(p => p.type === 'vendor' || !p.type) : (dynamicSettings?.suppliers || []);
              return vList.map(p => {
                const label = p.vendorName || p.name || p.title || (typeof p === 'string' ? p : "");
                const safeLabel = typeof label === 'object' ? "—" : (label || "—");
                const value = p.value || p._id || String(safeLabel);
                return <option key={value} value={value}>{String(label)}</option>
              });
            })()}
          </select>
        </div>
      )}

      {/* Customers / Projects Dropdown */}
      {/* {showCustomerFilter !== false && (
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 flex items-center gap-2">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-48"
            value={filters.customer || "all"}
            onChange={e => handleFilterChange('customer', e.target.value)}
          >
            <option value="all">All Projects/Customers</option>
            {(() => {
              const rawList = parties ? parties.filter(p => p.type === 'customer') : (projects || dynamicSettings?.projectsLocal || dynamicSettings?.projects || []);
              
              // Deduplicate by customer name
              const uniqueCustomers = [];
              const seenNames = new Set();
              
              rawList.forEach(p => {
                const label = p.customerName || p.name || p.title || (typeof p === 'string' ? p : "");
                const safeLabel = typeof label === 'object' ? "—" : (label || "—");
                const nameKey = String(safeLabel).toLowerCase().trim();
                
                if (nameKey && nameKey !== "—" && !seenNames.has(nameKey)) {
                  seenNames.add(nameKey);
                  // Use the name as the value so the filter matches all projects for this customer
                  uniqueCustomers.push({ label: String(safeLabel), value: String(safeLabel) });
                }
              });

              return uniqueCustomers.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ));
            })()}
          </select>
        </div>
      )} */}


      {showBack && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onBackClick}
          className="h-9 px-4 text-[10px] font-black uppercase tracking-widest"
          leftIcon={<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M15 19l-7-7 7-7" /></svg>}
        >
          BACK
        </Button>
      )}
      {children}
    </FilterSection>
  );
};